import { toast } from "sonner";

interface SaleTransaction {
  billNo?: string;
  memoNo?: string;
  memo_no?: string;
  invoiceNo?: string;
  invoice_no?: string;
  date?: string;
  txnDate?: string;
  txn_date?: string;
  time?: string;
  txnTime?: string;
  txn_time?: string;
  productName?: string;
  product_name?: string;
  fuelProduct?: string;
  qty?: number | string;
  quantity?: number | string;
  rate?: number | string;
  price?: number | string;
  amount?: number | string;
  total?: number | string;
  totalAmount?: number | string;
  total_amount?: number | string;
  discount?: number | string;
  mobile?: string;
  customerMobile?: string;
  customer_mobile?: string;
  indentNo?: string;
  indent_no?: string;
  customerName?: string;
  customer_name?: string;
  vehicleNo?: string;
  vehicle_no?: string;
  employeeName?: string;
  employee_name?: string;
  employee?: string;
  taxableAmt?: number | string;
  taxable_amount?: number | string;
  totalInWords?: string;
  bankName?: string;
  bankAcc?: string;
  bankIfsc?: string;
  cgst?: number | string;
  sgst?: number | string;
}

interface OrganizationDetails {
  organizationName?: string;
  name?: string;
  gstin?: string;
  orgGst?: string;
  contactNumber?: string;
  orgContact?: string;
  bankName?: string;
  bankAccountNo?: string;
  bankAcc?: string;
  bankIfscCode?: string;
  bankIfsc?: string;
  company_address?: string;
  orgAddress?: string;
  bank_branch?: string;
  bankBranch?: string;
}

// Convert numbers to Indian Rupees Words representation
export function convertNumberToWords(amount: number): string {
  const words = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  if (amount === 0) return "Zero Rupees Only";

  let num = Math.floor(amount);
  const paise = Math.round((amount - num) * 100);

  function getBelowHundred(n: number): string {
    if (n < 20) return words[n];
    return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + words[n % 10] : "");
  }

  function getBelowThousand(n: number): string {
    if (n === 0) return "";
    let str = "";
    if (n >= 100) {
      str += words[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n > 0) {
      str += getBelowHundred(n);
    }
    return str.trim();
  }

  let wordsStr = "";
  if (num >= 10000000) {
    wordsStr += getBelowThousand(Math.floor(num / 10000000)) + " Crore ";
    num %= 10000000;
  }
  if (num >= 100000) {
    wordsStr += getBelowThousand(Math.floor(num / 100000)) + " Lakh ";
    num %= 100000;
  }
  if (num >= 1000) {
    wordsStr += getBelowThousand(Math.floor(num / 1000)) + " Thousand ";
    num %= 1000;
  }
  if (num > 0) {
    wordsStr += getBelowThousand(num);
  }

  wordsStr = wordsStr.trim() + " Rupees";
  if (paise > 0) {
    wordsStr += " and " + getBelowHundred(paise) + " Paise";
  }
  return wordsStr + " Only";
}

export class PrintService {
  private static cachedOrg: any = null;

  private static async getOrgDetails(): Promise<any> {
    if (this.cachedOrg) return this.cachedOrg;
    try {
      const res = await fetch("/api/organization-details");
      const json = await res.json();
      if (json.ok && json.rows && json.rows.length > 0) {
        this.cachedOrg = json.rows[0];
        return this.cachedOrg;
      }
    } catch (e) {
      console.error("Failed to fetch org details:", e);
    }
    return null;
  }

  /**
   * CRITICAL: window.open() MUST be called synchronously within the user-click handler.
   * After any await, browsers drop the user-gesture context and block popups.
   *
   * Strategy:
   *  1. Open the window synchronously (before any await) — shows a loading state
   *  2. Fetch templates + org details asynchronously
   *  3. Write the compiled HTML into the already-open window
   *  4. Call print() on it
   */
  private static async triggerPrint(
    type: "bill" | "invoice",
    txn: SaleTransaction,
    org?: OrganizationDetails,
    printWin?: Window | null
  ) {
    // printWin is pre-opened synchronously by printBill/printInvoice before this async call
    const printWindow = printWin;

    if (!printWindow) {
      window.alert(
        "❌ Pop-up Blocked!\n\n" +
        "Your browser is blocking the print window.\n\n" +
        "To fix this:\n" +
        "1. Look at your browser address bar for a 🚫 or popup icon\n" +
        "2. Click it and choose 'Always allow pop-ups from localhost'\n" +
        "3. Then click Print again\n\n" +
        "(Or press F12 → Console to see the error)"
      );
      return;
    }

    try {
      // ── Fetch templates ──
      const res = await fetch("/api/print-templates");
      const json = await res.json();
      if (!json.success || !json.rows || json.rows.length === 0) {
        throw new Error("No print templates found on server. Check your Print Templates settings.");
      }

      // ── Find active template ──
      const activeTemplate = json.rows.find((t: any) => t.type === type && t.isActive);
      if (!activeTemplate) {
        throw new Error(
          `No active "${type}" template found. Go to Print Templates → select a template → click Activate.`
        );
      }

      // ── Fetch org details ──
      const fetchedOrg = await this.getOrgDetails();
      const orgName    = org?.organizationName || org?.name || fetchedOrg?.company_name || fetchedOrg?.organization_name || "PETROL ONE BUNK";
      const orgGst     = org?.gstin || org?.orgGst || fetchedOrg?.gst_number || fetchedOrg?.tin_gst_number || "GSTIN NOT RECORDED";
      const orgContact = org?.contactNumber || org?.orgContact || fetchedOrg?.contact_number || fetchedOrg?.phone_number || "+91 99999 88888";
      const bankName   = org?.bankName || fetchedOrg?.bank_name || "STATE BANK OF INDIA";
      const bankAcc    = org?.bankAccountNo || org?.bankAcc || fetchedOrg?.bank_account_no || fetchedOrg?.bankAcc || "390099887711";
      const bankIfsc   = org?.bankIfscCode || org?.bankIfsc || fetchedOrg?.bank_ifsc || fetchedOrg?.bankIfsc || "SBIN0001234";
      const bankBranch = org?.bankBranch || org?.bank_branch || fetchedOrg?.bank_branch || "NO BRANCH AVAILABLE";
      const orgAddress = org?.orgAddress || org?.company_address || fetchedOrg?.company_address || "NO ADDRESS AVAILABLE";

      // ── Normalize transaction values ──
      const qtyNum   = Number(txn.qty || txn.quantity || 0);
      const priceNum = Number(txn.rate || txn.price || 0);
      const totalNum = Number(txn.total || txn.amount || txn.totalAmount || txn.total_amount || (qtyNum * priceNum));
      const gstAmt   = totalNum - (totalNum / 1.18);
      const halfGst  = (gstAmt / 2).toFixed(3);
      const taxableAmt = (totalNum - gstAmt).toFixed(3);

      const logoMap: Record<string, string> = {
        indianoil: "/logo-indianoil.svg",
        hp: "/logo-hp.svg",
        bpcl: "/logo-bpcl.svg",
        reliance: "/logo-reliance.svg",
        custom: "/brand-logo.png"
      };
      const logoUrl = logoMap[activeTemplate.logoType || "custom"] || "/brand-logo.png";
      const isCustomLogo = !activeTemplate.logoType || activeTemplate.logoType === "custom";

      const normalizedData: Record<string, string> = {
        orgName, orgGst, orgContact, orgAddress,
        bankName, bankAcc, bankIfsc, bankBranch,
        logoUrl,
        logoUrlLeft:  isCustomLogo ? "/logo-indianoil.svg" : logoUrl,
        logoUrlRight: "",
        logoLeftStyle: "",
        logoRightStyle: "display: none;",
        logoRightContainerStyle: "display: none;",
        headerBannerStyle: isCustomLogo ? "" : "justify-content: center;",
        billNo: txn.billNo || txn.memoNo || txn.memo_no || txn.invoiceNo || txn.invoice_no || "N/A",
        date: txn.date || txn.txnDate || txn.txn_date || new Date().toISOString().slice(0, 10),
        time: txn.time || txn.txnTime || txn.txn_time || new Date().toLocaleTimeString(),
        product: txn.productName || txn.product_name || txn.fuelProduct || "PETROL",
        qty: qtyNum.toFixed(2),
        price: priceNum.toFixed(2),
        total: totalNum.toFixed(2),
        cgst: txn.cgst != null ? String(txn.cgst) : halfGst,
        sgst: txn.sgst != null ? String(txn.sgst) : halfGst,
        discount: txn.discount != null ? Number(txn.discount).toFixed(3) : "0.00",
        mobile: txn.mobile || txn.customerMobile || txn.customer_mobile || "N/A",
        indentNo: txn.indentNo || txn.indent_no || "N/A",
        customerName: txn.customerName || txn.customer_name || "GUEST CUSTOMER",
        vehicleNo: txn.vehicleNo || txn.vehicle_no || "N/A",
        employee: txn.employeeName || txn.employee_name || txn.employee || "N/A",
        taxableAmt: String(txn.taxableAmt || txn.taxable_amount || taxableAmt),
        totalInWords: txn.totalInWords || convertNumberToWords(totalNum),
        customerGst: "N/A",
        customerMobile: txn.mobile || txn.customerMobile || txn.customer_mobile || "N/A",
        customerAddress: "Local Address"
      };

      // ── Replace placeholders (handles {{ key }} with spaces too) ──
      let compiledHtml = activeTemplate.htmlStructure;
      Object.entries(normalizedData).forEach(([key, val]) => {
        const re = new RegExp(`{{\\s*${key}\\s*}}`, "g");
        compiledHtml = compiledHtml.replace(re, String(val ?? ""));
      });
      // Clear any remaining unmatched placeholders
      compiledHtml = compiledHtml.replace(/{{\s*[a-zA-Z0-9_\- ]+\s*}}/g, "");

      // ── Write final HTML into already-open window ──
      const printContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Print - ${activeTemplate.name}</title>
  <style>
    @media print { body { margin: 0; padding: 0; } @page { margin: 0; } }
    ${activeTemplate.styles || ""}
  </style>
</head>
<body>${compiledHtml}</body>
</html>`;

      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Trigger print — with both onload and a timeout fallback
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
      setTimeout(() => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.focus();
            printWindow.print();
          }
        } catch (_) { /* already printed via onload */ }
      }, 700);

    } catch (err: any) {
      console.error("[PrintService] Error:", err);
      // Show error inside the print window so user knows what went wrong
      if (printWindow && !printWindow.closed) {
        printWindow.document.open();
        printWindow.document.write(`<!DOCTYPE html><html><body style="font-family:sans-serif;padding:2rem;color:#c00;">
          <h3>&#9888; Print Failed</h3>
          <p><strong>${err.message}</strong></p>
          <hr/>
          <p style="color:#555;">Tip: Go to <strong>Print Templates</strong> in the sidebar and make sure a template is activated for the <em>${type}</em> type.</p>
        </body></html>`);
        printWindow.document.close();
      }
      toast.error(err.message || "Failed to generate print preview");
    }
  }

  // Trigger thermal receipt bill print
  public static printBill(txn: SaleTransaction, org?: OrganizationDetails) {
    // Open window synchronously here, BEFORE the async triggerPrint call
    const printWin = window.open("", "_blank", "width=820,height=650,scrollbars=yes,resizable=yes");
    if (printWin) {
      printWin.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading...</title>
        <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8f8f8;}
        .msg{text-align:center;color:#555;}</style></head>
        <body><div class="msg"><h3>&#9203; Preparing print...</h3><p>Please wait.</p></div></body></html>`);
    }
    this.triggerPrint("bill", txn, org, printWin);
  }

  // Trigger A4 invoice print
  public static printInvoice(txn: SaleTransaction, org?: OrganizationDetails) {
    // Open window synchronously here, BEFORE the async triggerPrint call
    const printWin = window.open("", "_blank", "width=820,height=650,scrollbars=yes,resizable=yes");
    if (printWin) {
      printWin.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading...</title>
        <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8f8f8;}
        .msg{text-align:center;color:#555;}</style></head>
        <body><div class="msg"><h3>&#9203; Preparing print...</h3><p>Please wait.</p></div></body></html>`);
    }
    this.triggerPrint("invoice", txn, org, printWin);
  }
}

